import type { TvChannel } from '@setplex/tria-api'
import type {
  LoadFeaturedParams,
  LoadPageParams,
  TvChannelPageMeta,
} from './index.h'

import { A, D, pipe } from '@mobily/ts-belt'
import { api } from '@setplex/tria-api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { concatById, toArray } from '~/shared/helpers'
import { isChannelsCarousel } from './lib'

// all loaded channels, mapped by id
export const addToAll = createEvent<TvChannel | TvChannel[]>()
export const $all = createStore<[Map<number, TvChannel>]>([new Map()])

// loaded channels by categories, with metadata (isLast, hasMore)
export const $byCategory = createStore<
  [Map<number, TvChannel[]>, Map<number, TvChannelPageMeta>]
>([new Map(), new Map()])

// load single channel by id
export const loadOneFx = attach({
  mapParams: (id: number) => ({ ids: [id] }),
  effect: api.tv.getTvChannelsByIdFx,
})

// load many channel by ids
export const loadManyFx = attach({
  mapParams: (ids: number[]) => ({ ids }),
  effect: api.tv.getTvChannelsByIdFx,
})

// load many channels from many bundles by ids
// eslint-disable-next-line effector/strict-effect-handlers
export const loadSetsFx = createEffect<number[], TvChannel[]>(async (ids) => {
  // load bundles info, to get channels ids
  const bundles = await Promise.all(
    A.map(ids, (id) => api.tvBundles.getTvBundleInfoFx({ bundleId: id }))
  )

  // extract channels ids from bundles infos
  const channelsIds = pipe(
    bundles,
    A.map(D.getUnsafe('channelIds')),
    A.flat,
    A.uniq
  )

  // load many channels by ids
  return await loadManyFx(channelsIds)
})

// load channels by category by page
export const loadPageFx = attach({
  mapParams: (params: LoadPageParams) => D.deleteKey(params, 'byCategoryId'),
  effect: api.tv.getAllTvChannelsByPageAbortableFx,
})

// load featured channels
export const loadFeaturedFx = attach({
  mapParams: (_: LoadFeaturedParams) => undefined, // `getFeaturedCarouselFx`'s params is `void`
  effect: api.banners.getFeaturedCarouselFx,
})

// fetch channel's url by id
export const fetchUrlFx = attach({
  mapParams: (id: number) => ({ channelId: id }),
  effect: api.tv.getTvChannelUrlAbortableFx,
})

// when new channels are loaded - add them to requested category in map
sample({
  clock: loadPageFx.done,
  source: $byCategory,
  fn: (
    [channels, meta],
    { params, result: { content, last, number, size, totalElements } }
  ) => {
    const categoryId = params.byCategoryId

    const existing = channels.get(categoryId)
    channels.set(categoryId, concatById(existing)(content))
    meta.set(categoryId, {
      isLast: Boolean(last),
      hasMore: !last,
      page: number,
      size,
      totalElements,
    })

    return [channels, meta] as const
  },
  target: $byCategory,
})

// when featured carousel is loaded - add channels to featured category in map
sample({
  clock: loadFeaturedFx.done,
  source: $byCategory,
  fn: (box, { params, result: carousels }) => {
    const content: TvChannel[] = pipe(
      carousels,
      A.filter(isChannelsCarousel),
      A.map(({ content }) => content),
      A.flat,
      A.map(({ channel }) => channel)
    )

    if (content.length === 0) return box
    const [channels, meta] = box

    const categoryId = params.byCategoryId
    const existing = channels.get(categoryId)
    channels.set(categoryId, concatById(existing)(content))
    meta.set(categoryId, { isLast: true, hasMore: false })

    return [channels, meta] as const
  },
  target: $byCategory,
})

// update channels in byCategory map, if we got new channel
// * listen for ALL channels responses (not only on epg page)
sample({
  clock: api.tv.getTvChannelsByIdFx.doneData,
  source: $byCategory,
  fn: ([channels, meta], got) => {
    const updated = new Map(A.map(got, (channel) => [channel.id, channel]))
    for (const [categoryId, categoryChannels] of channels) {
      let changed = false
      for (let i = 0; i < categoryChannels.length; i++) {
        const channel = categoryChannels[i]
        const updatedChannel = updated.get(channel.id)
        if (updatedChannel != null) {
          changed = true
          categoryChannels.splice(i, 1, updatedChannel)
        }
      }
      if (changed) channels.set(categoryId, categoryChannels.slice())
    }
    return [channels, meta] as const
  },
  target: $byCategory,
})

// add loaded channels to all channels map
// * listen for ALL pageable responses (not only on epg page)
sample({
  clock: [
    api.tv.getAllTvChannelsByPageAbortableFx.doneData,
    api.tv.getChannelsByCategoryIdAbortableFx.doneData,
  ],
  fn: ({ content }) => content,
  target: addToAll,
})

// add loaded channels to all channels map
// * listen for ALL channels responses (not only on epg page)
sample({
  clock: api.tv.getTvChannelsByIdFx.doneData,
  target: addToAll,
})

// add loaded featured channels to all channels map
// * listen for ALL channels responses (not only on epg page)
sample({
  clock: api.banners.getFeaturedCarouselFx.doneData,
  fn: (carousels) =>
    pipe(
      carousels,
      A.filter(isChannelsCarousel),
      A.map((carousel) => carousel.content),
      A.flat,
      A.map((item) => item.channel!)
    ),
  target: addToAll,
})

// update all channels map
sample({
  clock: addToAll,
  source: $all,
  fn: ([all], channels) => {
    for (const channel of toArray(channels)) {
      all.set(channel.id, channel) // update or set channel by id
    }
    return [all] as const
  },
  target: $all,
})
